import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Play, FileDown, Lock, ChevronRight, ArrowLeft, ShoppingCart, CheckCircle, Circle, Eye } from 'lucide-react';
import { getTrainingContent, updateTrainingProgress, getTrainingProgress } from '../utils/api';
import { toast } from 'sonner';
import AgentLayout from '../components/AgentLayout';

const TIER_CONFIG = {
  basic: { label: 'Basic', color: 'bg-blue-100 text-blue-800 border-blue-200', accent: 'text-brand-blue', ring: 'ring-blue-200' },
  advanced: { label: 'Advanced', color: 'bg-orange-100 text-orange-800 border-orange-200', accent: 'text-brand-orange', ring: 'ring-orange-200' },
  premium: { label: 'Premium', color: 'bg-purple-100 text-purple-800 border-purple-200', accent: 'text-purple-700', ring: 'ring-purple-200' },
};

const TYPE_ICON = {
  article: BookOpen,
  video: Play,
  guide: FileDown,
};

const TrainingContent = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState([]);
  const [purchasedTiers, setPurchasedTiers] = useState([]);
  const [progress, setProgress] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filterTier, setFilterTier] = useState('all');

  const token = localStorage.getItem('agentToken');

  useEffect(() => {
    if (!token) { navigate('/agent/login'); return; }
    loadContent();
  }, [navigate]);

  const loadContent = async () => {
    try {
      setIsLoading(true);
      const [contentRes, progressRes] = await Promise.all([
        getTrainingContent(token),
        getTrainingProgress(token),
      ]);
      setContent(contentRes.content || []);
      setPurchasedTiers(contentRes.purchasedTiers || []);
      const progressMap = {};
      (progressRes.progress || []).forEach(p => { progressMap[p.contentId] = p; });
      setProgress(progressMap);
    } catch (e) {
      toast.error('Failed to load training content');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewItem = async (item) => {
    setSelectedItem(item);
    if (item.accessible && !progress[item.id]) {
      try {
        await updateTrainingProgress(item.id, 'viewed', token);
        setProgress(prev => ({ ...prev, [item.id]: { contentId: item.id, status: 'viewed' } }));
      } catch (_) {}
    }
  };

  const handleMarkComplete = async (contentId) => {
    try {
      await updateTrainingProgress(contentId, 'completed', token);
      setProgress(prev => ({ ...prev, [contentId]: { ...prev[contentId], contentId, status: 'completed' } }));
      toast.success('Marked as complete!');
    } catch (_) {
      toast.error('Failed to update progress');
    }
  };

  const getProgressIcon = (itemId) => {
    const p = progress[itemId];
    if (!p) return null;
    if (p.status === 'completed') return <CheckCircle size={14} className="text-green-500" />;
    if (p.status === 'viewed') return <Eye size={14} className="text-blue-400" />;
    return null;
  };

  // Calculate progress stats
  const accessibleContent = content.filter(c => c.accessible);
  const completedCount = accessibleContent.filter(c => progress[c.id]?.status === 'completed').length;
  const viewedCount = accessibleContent.filter(c => progress[c.id]?.status === 'viewed').length;
  const progressPct = accessibleContent.length > 0 ? Math.round((completedCount / accessibleContent.length) * 100) : 0;

  const filteredContent = filterTier === 'all'
    ? content
    : content.filter(c => c.packageTier === filterTier);

  const groupedContent = {
    basic: filteredContent.filter(c => c.packageTier === 'basic'),
    advanced: filteredContent.filter(c => c.packageTier === 'advanced'),
    premium: filteredContent.filter(c => c.packageTier === 'premium'),
  };

  if (isLoading) {
    return (
      <AgentLayout title="Training">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
        </div>
      </AgentLayout>
    );
  }

  // Detail view
  if (selectedItem) {
    const itemProgress = progress[selectedItem.id];
    const isCompleted = itemProgress?.status === 'completed';
    return (
      <AgentLayout title="Training">
        <div data-testid="training-detail-view">
          <button
            onClick={() => setSelectedItem(null)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-brand-blue mb-4 min-h-[44px] transition"
            data-testid="back-to-list-btn"
          >
            <ArrowLeft size={16} /> Back to Training Library
          </button>
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            {/* Header */}
            <div className="border-b px-6 py-5">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${TIER_CONFIG[selectedItem.packageTier]?.color}`}>
                  {TIER_CONFIG[selectedItem.packageTier]?.label}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 capitalize">
                  {selectedItem.contentType}
                </span>
                {isCompleted && (
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 flex items-center gap-1">
                    <CheckCircle size={12} /> Completed
                  </span>
                )}
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900" data-testid="training-detail-title">{selectedItem.title}</h2>
              <p className="text-gray-500 mt-1 text-sm">{selectedItem.description}</p>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Video embed */}
              {selectedItem.contentType === 'video' && selectedItem.videoUrl && (
                <div className="mb-6 aspect-video rounded-lg overflow-hidden bg-gray-900" data-testid="training-video-embed">
                  <iframe
                    src={selectedItem.videoUrl}
                    title={selectedItem.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}

              {/* Downloadable guide */}
              {selectedItem.contentType === 'guide' && selectedItem.downloadUrl && (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="training-download-section">
                  <FileDown size={24} className="text-brand-blue flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">Downloadable Guide</p>
                    <p className="text-sm text-gray-500">Download this guide to view offline or print.</p>
                  </div>
                  <a
                    href={selectedItem.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 min-h-[44px] bg-brand-blue text-white rounded-lg font-semibold text-sm hover:bg-blue-700 transition flex items-center gap-2"
                    data-testid="download-guide-btn"
                  >
                    <FileDown size={16} /> Download
                  </a>
                </div>
              )}

              {/* Article body */}
              {selectedItem.body && (
                <div
                  className="prose prose-sm sm:prose max-w-none text-gray-700 training-article-body"
                  dangerouslySetInnerHTML={{ __html: selectedItem.body }}
                  data-testid="training-article-body"
                />
              )}

              {/* Mark as Complete */}
              {selectedItem.accessible && (
                <div className="mt-8 pt-6 border-t flex items-center justify-between" data-testid="progress-actions">
                  {isCompleted ? (
                    <div className="flex items-center gap-2 text-green-600 font-semibold text-sm">
                      <CheckCircle size={18} /> You've completed this content
                    </div>
                  ) : (
                    <button
                      onClick={() => handleMarkComplete(selectedItem.id)}
                      className="flex items-center gap-2 px-6 py-3 min-h-[44px] bg-green-600 text-white rounded-lg font-semibold text-sm hover:bg-green-700 transition"
                      data-testid="mark-complete-btn"
                    >
                      <CheckCircle size={16} /> Mark as Complete
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </AgentLayout>
    );
  }

  // List view
  return (
    <AgentLayout title="Training">
      <div data-testid="training-content-page">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Training Library</h2>
          <p className="text-gray-500 text-sm sm:text-base">Access your purchased training materials - articles, videos, and guides.</p>
        </div>

        {/* Progress Summary */}
        {purchasedTiers.length > 0 && accessibleContent.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-5 mb-6" data-testid="training-progress-summary">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-1">Your Progress</h3>
                <p className="text-xs text-gray-500">
                  {completedCount} of {accessibleContent.length} items completed
                  {viewedCount > 0 && ` | ${viewedCount} in progress`}
                </p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-brand-blue">{progressPct}%</span>
              </div>
            </div>
            <div className="mt-3 w-full bg-gray-200 rounded-full h-2.5" data-testid="progress-bar">
              <div
                className="h-2.5 rounded-full transition-all duration-500"
                style={{
                  width: `${progressPct}%`,
                  background: progressPct === 100 ? '#22c55e' : 'linear-gradient(90deg, #0047AB, #4169E1)',
                }}
              />
            </div>
          </div>
        )}

        {/* No purchases CTA */}
        {purchasedTiers.length === 0 && (
          <div className="bg-gradient-to-r from-blue-50 to-orange-50 border border-blue-200 rounded-xl p-6 mb-6 text-center" data-testid="no-purchases-cta">
            <ShoppingCart size={32} className="mx-auto text-brand-blue mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-1">No Training Purchased Yet</h3>
            <p className="text-gray-600 text-sm mb-4">Unlock training materials by purchasing a package from the Billing page.</p>
            <button
              onClick={() => navigate('/agent/billing')}
              className="px-6 py-2 min-h-[44px] bg-brand-blue text-white rounded-lg font-semibold text-sm hover:bg-blue-700 transition"
              data-testid="go-to-billing-btn"
            >
              View Training Packages
            </button>
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="training-filter-tabs">
          {[
            { id: 'all', label: 'All Content' },
            { id: 'basic', label: 'Basic' },
            { id: 'advanced', label: 'Advanced' },
            { id: 'premium', label: 'Premium' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilterTier(tab.id)}
              className={`px-4 py-2 min-h-[44px] rounded-lg text-sm font-semibold transition ${
                filterTier === tab.id
                  ? 'bg-brand-blue text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`filter-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content by tier */}
        {['basic', 'advanced', 'premium'].map(tier => {
          const items = groupedContent[tier];
          if (items.length === 0) return null;
          const cfg = TIER_CONFIG[tier];
          const tierPurchased = purchasedTiers.includes(tier);

          return (
            <div key={tier} className="mb-8" data-testid={`tier-section-${tier}`}>
              <div className="flex items-center gap-3 mb-4">
                <h3 className={`text-lg font-bold ${cfg.accent}`}>{cfg.label} Training</h3>
                {tierPurchased ? (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">Unlocked</span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 flex items-center gap-1">
                    <Lock size={10} /> Locked
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map(item => {
                  const Icon = TYPE_ICON[item.contentType] || BookOpen;
                  return (
                    <div
                      key={item.id}
                      className={`bg-white rounded-xl border shadow-sm hover:shadow-md transition cursor-pointer group relative ${
                        !item.accessible ? 'opacity-75' : ''
                      }`}
                      onClick={() => {
                        if (item.accessible) {
                          handleViewItem(item);
                        } else {
                          toast.info('Purchase the required training package to access this content.');
                        }
                      }}
                      data-testid={`content-card-${item.id}`}
                    >
                      {!item.accessible && (
                        <div className="absolute inset-0 bg-gray-50/60 rounded-xl flex items-center justify-center z-10">
                          <div className="flex flex-col items-center gap-2">
                            <Lock size={24} className="text-gray-400" />
                            <span className="text-xs font-semibold text-gray-500">Purchase to Unlock</span>
                          </div>
                        </div>
                      )}
                      <div className="p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                            item.contentType === 'video' ? 'bg-red-100 text-red-600' :
                            item.contentType === 'guide' ? 'bg-green-100 text-green-600' :
                            'bg-blue-100 text-brand-blue'
                          }`}>
                            <Icon size={18} />
                          </div>
                          <span className="text-xs font-semibold text-gray-400 uppercase">{item.contentType}</span>
                        </div>
                        <h4 className="text-sm font-bold text-gray-900 mb-1 group-hover:text-brand-blue transition line-clamp-2">{item.title}</h4>
                        <p className="text-xs text-gray-500 line-clamp-2">{item.description}</p>
                        {item.accessible && (
                          <div className="mt-3 flex items-center justify-between">
                            <div className="flex items-center gap-1 text-xs font-semibold text-brand-blue">
                              View Content <ChevronRight size={14} />
                            </div>
                            {getProgressIcon(item.id) && (
                              <div className="flex items-center gap-1 text-xs" data-testid={`progress-icon-${item.id}`}>
                                {getProgressIcon(item.id)}
                                <span className={progress[item.id]?.status === 'completed' ? 'text-green-600 font-semibold' : 'text-blue-400'}>
                                  {progress[item.id]?.status === 'completed' ? 'Done' : 'Viewed'}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {filteredContent.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <BookOpen size={40} className="mx-auto mb-3" />
            <p>No training content available for this filter.</p>
          </div>
        )}
      </div>
    </AgentLayout>
  );
};

export default TrainingContent;
